import { ref } from '@nuxtjs/composition-api';
import { IMotorcycleManualSearchSelection } from '@shared/types/IVehicleSelection';
import { PartsDbVehicleSummary } from '@unified-commerce/gpc-vue-storefront-parts-db';
import {
  AlgoliaSearchVehicleResponse,
  getFacets,
  getFacetStats,
  useVehicleSearch,
} from '@unified-commerce/gpc-vue-storefront-search';

import {
  fullParams,
  makeParams,
  modelParams,
  toPartsDbVehicleSummary,
  yearParams,
} from './helpers';

interface VehicleOption {
  title: string;
  value: string;
}

interface ModelVehicleOptions {
  submodels: VehicleOption[];
  models: VehicleOption[];
}

const results = ref<PartsDbVehicleSummary[] | null>(null);

// eslint-disable-next-line max-lines-per-function
const useVehicleManualSearch = () => {
  const { search: fieldSearch, result: fieldResult } = useVehicleSearch(
    'vehicle-manual-search-fields',
  );
  const { search, loading, result } = useVehicleSearch('vehicle-manual-search');

  const getMakes = async (): Promise<VehicleOption[]> => {
    await fieldSearch(makeParams());

    return getFacets(fieldResult.value, 'Make');
  };

  const getModels = async (
    selection: IMotorcycleManualSearchSelection,
  ): Promise<ModelVehicleOptions> => {
    await fieldSearch(modelParams(selection) as any);

    return {
      submodels: getFacets(fieldResult.value, 'Submodel'),
      models: getFacets(fieldResult.value, 'Model'),
    };
  };

  const getYears = async (
    selection: IMotorcycleManualSearchSelection,
  ): Promise<VehicleOption[]> => {
    await fieldSearch(yearParams(selection) as any);

    const minYear = getMinYear(fieldResult.value);
    const maxYear = getMaxYear(fieldResult.value);

    if (!minYear) {
      return [];
    }

    const options = [];
    for (let year = minYear; year <= maxYear; year++) {
      options.push({ title: year.toString(), value: year.toString() });
    }

    return options;
  };

  const getMinYear = (response: AlgoliaSearchVehicleResponse) => {
    const yearFromStats = getFacetStats(response, 'YearFrom');
    return yearFromStats?.min;
  };

  const getMaxYear = (response: AlgoliaSearchVehicleResponse) => {
    const yearToStats = getFacetStats(response, 'YearTo');
    return typeof yearToStats?.max === 'number' ? yearToStats.max : new Date().getFullYear();
  };

  const searchAll = async (selection: IMotorcycleManualSearchSelection) => {
    await search(fullParams(selection) as any);

    results.value = (result.value?.results?.[0]?.hits ?? []).map((record) => ({
      ...toPartsDbVehicleSummary(record),
      exactYear: Number(selection.year),
    }));
  };

  return {
    getMakes,
    getModels,
    getYears,
    search: searchAll,
    results,
    loading,
  };
};

export default useVehicleManualSearch;
